// Справка „Инвентаризации" (бившият списък на операцията): KPI (само
// активните), търсачка, филтри В ЗАГЛАВИЯТА НА КОЛОНИТЕ (Склад · Въвел ·
// Причина · Излишък · Липси · Статус) с фасетни броячи и ред „Филтри: …" над
// таблицата, сортиране по заглавията, „Групирай по склад" (лентите „Кухня · 2
// инвентаризации", по подразбиране ИЗКЛЮЧЕНО), „Покажи още", Excel. Клик на ред
// отваря документа; „Печат" и менюто „⋯" (Принтирай · Преглед · Редактирай ·
// Анулирай) са на реда. Периодът по подразбиране е „Този месец".
import { useMemo, useState, type FC } from "react";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  EuroOutlined,
  ReconciliationOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { useStocktakesReport } from "../../../../hooks/useInventoryStocktakes";
import type { StocktakeDocument } from "../../../../api/inventoryStocktakes";
import { exportToExcel } from "../../../../utils/excelExport";
import { InventoryTheme } from "../../components/InventoryTheme";
import type { LeaveGuard } from "../../components/shell/InventoryShellContext";
import { PageHeader } from "../../components/list/PageHeader";
import { EmptyScreen, KpiGrid, SearchBox, SectionCard, type KpiCard } from "../../components/list/ListBits";
import type { RowAction } from "../../components/list/RowActionsMenu";
import { ReportToolbar } from "../../components/list/ReportToolbar";
import { refreshOf } from "../../components/list/reportRefresh";
import { periodOfPreset, periodToParams, type Period } from "../../components/period/periodPresets";
import { CheckBox } from "../../components/filters/CheckBox";
import { FilterSummaryLine } from "../../components/filters/FilterSummaryLine";
import { allSections, useColumnFilters, type ColumnSections } from "../../components/filters/useColumnFilters";
import { useShowMore } from "../../components/list/useShowMore";
import { fmtDateTime } from "../../components/dateTime";
import { useStocktakeDocument } from "../../stocktake/components/useStocktakeDocument";
import { formatSignedMoney, isStocktakeAnnulled } from "../../stocktake/components/stocktakeMeta";
import { ReportLoading, ReportNoMatch, ShowMoreRow } from "../components/ReportListBits";
import {
  EMPTY_STOCKTAKE_FILTERS,
  TEXT_SORT_KEYS,
  searchStocktakes,
  sortStocktakes,
  statusKeyOf,
  stocktakeFilterSpecs,
  stocktakeTotals,
  toListRows,
  type StocktakeFilterKey,
  type StocktakeSort,
  type StocktakeSortKey,
} from "./stocktakesReportModel";
import { StocktakesReportTable, type StocktakesDensity } from "./StocktakesReportTable";

// Inventory.tsx подава на всяка страница общите props на layout-а; справката
// чете нужното от `useInventoryShell`/`useViewport` и ги игнорира.
type LayoutProps = {
  onOpenMobileMenu?: () => void;
  isNarrow?: boolean;
  warehouseId?: number | null;
  warehouseName?: string | null;
  onRegisterLeaveGuard?: (guard: LeaveGuard | null) => void;
};

export const StocktakesReport: FC<LayoutProps> = () => {
  const { t } = useTranslation();
  const { isNarrow, isWide } = useViewport();
  const stocktakeDocument = useStocktakeDocument();

  const [period, setPeriod] = useState<Period>(() => periodOfPreset("thisMonth"));
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<StocktakeSort>({ key: "date", dir: "desc" });
  // по подразбиране ИЗКЛЮЧЕНО — справката тръгва плоска, групите се включват ръчно
  const [groupByWarehouse, setGroupByWarehouse] = useState(false);
  // Без склад → всички инвентаризации от всички складове (и анулираните)
  const stocktakesQuery = useStocktakesReport({
    ...periodToParams(period),
    status: "all",
  });
  const { data: stocktakes = [], isLoading } = stocktakesQuery;
  const { refresh, refreshing } = refreshOf(stocktakesQuery);

  const statusLabel = (value: string) =>
    value === "annulled"
      ? t("inventoryPage.stocktakeStatusAnnulled")
      : t("inventoryPage.stocktakeStatusActive");

  // ─── Филтри в колоните (броячите — фасетни, по периода след търсенето) ──
  const specs = useMemo(
    () =>
      stocktakeFilterSpecs({
        titles: {
          warehouses: t("inventoryPage.colWarehouse"),
          users: t("inventoryPage.filterEnteredBy"),
          reasons: t("inventoryPage.colReason"),
          surplus: t("inventoryPage.colSurplus"),
          shortage: t("inventoryPage.colShortage"),
          statuses: t("inventoryPage.colStatus"),
        },
        surplusLabel: (value) => (value === "yes" ? t("inventoryPage.cfWithSurplus") : t("inventoryPage.cfNoSurplus")),
        shortageLabel: (value) =>
          value === "yes" ? t("inventoryPage.cfWithShortage") : t("inventoryPage.cfNoShortage"),
        statusLabel,
      }),
    // statusLabel зависи само от t
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );
  const searched = useMemo(() => searchStocktakes(stocktakes, query), [stocktakes, query]);
  const filters = useColumnFilters({ specs, empty: EMPTY_STOCKTAKE_FILTERS, universe: stocktakes, base: searched });
  const shown = useMemo(() => sortStocktakes(filters.rows, sort), [filters.rows, sort]);
  // Ключ-низ: нов масив на всеки рендер би връщал „Покажи още" отначало
  const resetKey = JSON.stringify([
    period.from.valueOf(),
    period.to.valueOf(),
    filters.state,
    query,
    sort,
    groupByWarehouse,
  ]);
  const { visible, hiddenCount, showMore } = useShowMore(shown, resetKey);
  const listRows = toListRows(visible, shown, groupByWarehouse);
  const kpis = stocktakeTotals(stocktakes);
  const shownTotals = stocktakeTotals(shown);
  const clearAll = () => {
    filters.clearAll();
    setQuery("");
  };

  const sections: ColumnSections<StocktakeFilterKey> = {
    warehouses: filters.section("warehouses"),
    users: filters.section("users"),
    reasons: filters.section("reasons"),
    surplus: filters.section("surplus"),
    shortage: filters.section("shortage"),
    statuses: filters.section("statuses"),
  };

  const kpiCards: KpiCard[] = [
    { label: t("inventoryPage.kpiStocktakes"), value: String(kpis.count), Icon: ReconciliationOutlined },
    { label: t("inventoryPage.kpiSurplus"), value: String(kpis.surplus), Icon: ArrowUpOutlined },
    { label: t("inventoryPage.kpiShortage"), value: String(kpis.shortage), Icon: ArrowDownOutlined },
    {
      label: t("inventoryPage.colNetValue"),
      value: formatSignedMoney(kpis.net),
      Icon: EuroOutlined,
      tone: "brand",
    },
  ];

  const onSort = (key: StocktakeSortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: TEXT_SORT_KEYS.includes(key) ? "asc" : "desc" },
    );

  const actionsOf = (stocktake: StocktakeDocument): RowAction[] => {
    const base: RowAction[] = [
      {
        key: "print",
        label: t("inventoryPage.print"),
        onClick: () => stocktakeDocument.open(stocktake, { autoPrint: true }),
      },
      {
        key: "view",
        label: t("inventoryPage.viewDocument"),
        onClick: () => stocktakeDocument.open(stocktake),
      },
    ];
    if (isStocktakeAnnulled(stocktake)) return base;
    return [
      ...base,
      {
        key: "edit",
        label: t("inventoryPage.docEdit"),
        dividerBefore: true,
        onClick: () => stocktakeDocument.edit(stocktake),
      },
      {
        key: "annul",
        label: t("inventoryPage.docDelete"),
        danger: true,
        onClick: () => stocktakeDocument.requestAnnul(stocktake),
      },
    ];
  };

  const exportXls = () =>
    exportToExcel({
      filename: "inventarizacii",
      sheetName: t("inventoryPage.navStocktakesReport"),
      rows: shown,
      columns: [
        { header: "№", value: (s) => s.id },
        { header: t("inventoryPage.colDate"), value: (s) => fmtDateTime(s.date) },
        { header: t("inventoryPage.colWarehouse"), value: (s) => s.warehouseName ?? "" },
        { header: t("inventoryPage.filterEnteredBy"), value: (s) => s.createdByName ?? "" },
        { header: t("inventoryPage.colReason"), value: (s) => s.reason ?? "" },
        { header: t("inventoryPage.colSurplus"), value: (s) => s.surplusCount },
        { header: t("inventoryPage.colShortage"), value: (s) => s.shortageCount },
        { header: t("inventoryPage.colNet"), value: (s) => s.totalValueNoVat },
        { header: t("inventoryPage.colGross"), value: (s) => s.totalValueWithVat },
        { header: t("inventoryPage.colStatus"), value: (s) => statusLabel(statusKeyOf(s)) },
        {
          header: t("inventoryPage.annulReason"),
          value: (s) => (isStocktakeAnnulled(s) ? (s.deleteReason ?? "") : ""),
        },
      ],
    });

  const density: StocktakesDensity = isNarrow ? "cards" : isWide ? "full" : "medium";

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          kicker={t("inventoryPage.kickerReport")}
          title={t("inventoryPage.navStocktakesReport")}
          right={
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              filterSections={allSections(sections)}
              onExport={exportXls}
              exportDisabled={!shown.length}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <KpiGrid cards={kpiCards} />
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={query} onChange={setQuery} placeholder={t("inventoryPage.stocktakesSearchPh")} />
          </div>
          <SectionCard>
            <FilterSummaryLine
              countLabel={t("inventoryPage.groupStocktakesCount", { count: shown.length })}
              items={filters.summary}
              onClearAll={filters.clearAll}
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={groupByWarehouse}
                onClick={() => setGroupByWarehouse((on) => !on)}
                className="flex cursor-pointer items-center gap-2 rounded-md border-none bg-transparent p-0 text-[12.5px] leading-[normal] text-body outline-none hover:text-ink focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)]"
              >
                <CheckBox checked={groupByWarehouse} />
                {t("inventoryPage.groupByWarehouse")}
              </button>
              <span>
                {t("inventoryPage.colGross")}{" "}
                <span className="font-sheet font-bold text-brand-deep-ink">
                  {formatSignedMoney(shownTotals.gross)}
                </span>
              </span>
            </FilterSummaryLine>
            {isLoading ? (
              <ReportLoading />
            ) : stocktakes.length === 0 ? (
              <EmptyScreen Icon={ReconciliationOutlined} tone="brand" title={t("inventoryPage.noStocktakes")} />
            ) : (
              <StocktakesReportTable
                rows={listRows}
                density={density}
                sort={sort}
                onSort={onSort}
                filters={sections}
                totalNet={shownTotals.net}
                totalGross={shownTotals.gross}
                onOpen={(stocktake) => stocktakeDocument.open(stocktake)}
                onPrint={(stocktake) => stocktakeDocument.open(stocktake, { autoPrint: true })}
                actionsOf={actionsOf}
                footer={
                  shown.length ? (
                    <ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />
                  ) : (
                    <ReportNoMatch onClear={clearAll} />
                  )
                }
              />
            )}
          </SectionCard>
        </div>
        {stocktakeDocument.modals}
      </div>
    </InventoryTheme>
  );
};

export default StocktakesReport;
